import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Divider, FormLabel, IconButton, ListItemIcon, MenuItem, Stack, Typography } from '@mui/material';
import { AgyleLogoIcon } from '../../icons/icons';
import useOpenState from '../../../hooks/useOpenState';
import UserProperties, { UserPropertiesUtils } from './userProperties';
import { useEventListener } from '../../../hooks/useEventListener';
import Menu from '@mui/material/Menu';
import Avatar from '@mui/material/Avatar';
import Settings from '@mui/icons-material/Settings';
import Logout from '@mui/icons-material/Logout';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { canEnterAdminPage, hasOneOfPermission, hasPermission, Permissions, user } from '../../../store/reducers/user';
import makeStyles from '@mui/styles/makeStyles';
import { AgyleNamedIcon } from '../../icons/agyle';
import InfoIcon from '@mui/icons-material/Info';
import { ChangeCircle } from '@mui/icons-material';
import AboutInfo from './aboutInfo';
import { agylePref, saveCurrentTheme, saveSpecialties, specialties as specialtiesReducer } from '../../../store/reducers/preferences';
import AnnouncementPanel from './announcementPanel';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import StayCurrentPortraitIcon from '@mui/icons-material/StayCurrentPortrait';
import MessageBox from '../../../util/messageBox';
import AgyleBlockSwitch from '../../ui/switch/agyleBlockSwitch';
import { agyleBlockContext, setSnackbar } from '../../../store/reducers/globals';
import _ from 'lodash';
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';
import AgyleDictionaryField from '../../ui/selector/agyleDictionaryField';
import { filterOptions } from '../../../controls/agyle/ecds';
import useActiveModules from '../../../pages/centralPage/hooks/useActiveModules';
import UserAvatar from '../../../pages/centralPage/components/userAvatar';
import UserActiveSpecialties from '../../../pages/centralPage/components/userActiveSpecialties';
import { useTheme } from '@mui/styles';
import AgyleMenuList from '../../ui/selector/agyleMenuList';
import ActiveModules from '../../../pages/centralPage/components/activeModules';
import { getConfig } from '../../../util/configUtil';
import { CENTRAL_PAGE_PATH } from '../../../pages/centralPage/constants';

const useStyles = makeStyles((theme) => ({
    menuItem: {
        fontSize: 'small',
        width: '100%',
        justifyContent: 'left',
        '&.name': {
            cursor: 'auto',
            '&:hover': {
                backgroundColor: 'transparent',
            },
        },
        '&.agyleHub': {
            '& svg': {
                translate: 'scale(0.1)',
            },
        },
    },
    appLogo: {
        opacity: 1,
        fontSize: 'xx-large',
        '&.menuToggled': {
            opacity: 0.5,
        },
        '&.small': {
            fontSize: 24,
        },
    },
    backDrop: {
        // backdropFilter: theme.palette.blur,
    },
    divRow: {
        marginTop: 10,
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
    radioButtonLabel: {
        fontSize: 'small',
        marginLeft: 7,
    },
    radioButton: {
        marginLeft: 17,
    },
    textFieldStyle: {
        height: 14,
    },
    textFieldLabelStyle: {
        marginTop: -5,
    },
    avatarWithName: {
        gap: 10,
        flexDirection: 'column',
        justifyContent: 'center',
    },
    specTitle: {
        flexGrow: 1,
        display: 'flex',
        alignItems: 'center',
        textTransform: 'capitalize',
        justifyContent: 'space-between',
    },
    specsCountIndicator: {
        width: 20,
        height: 20,
        display: 'flex',
        fontWeight: 400,
        borderRadius: '50%',
        justifyContent: 'center',
        backgroundColor: theme.palette.primary.main,
        color: '#FFF',
    },
    modulesContainer: {
        flexWrap: 'wrap',
        maxWidth: '350px',
        maxHeight: '300px',
    },
    hubIcon: {
        marginLeft: 2,

        '& svg': {
            width: 16,
            height: 16,
        },
    },
}));

const AgyleMenuIcon = ({
    id = 'agyle-menu',
    transform = { horizontal: 'right', vertical: 'top' },
    anchor = { horizontal: 'right', vertical: 'bottom' },
    size = 'medium',
    whiteboardMode = false,
    onClick,
}) => {
    const { t } = useTranslation();
    const userObject = useSelector(user);
    const dispatch = useDispatch();
    const classes = useStyles();
    const theme = useTheme();
    const history = useHistory();
    const agylePreference = useSelector(agylePref);
    const blockContext = useSelector(agyleBlockContext);

    // Open States
    const [isUserPropertiesOpen, openUserProperties, closeUserProperties] = useOpenState(false, true);
    const [isAnnouncementsOpen, openAnnouncements, closeAnnouncements] = useOpenState(false, true);
    const [isAboutInformationOpen, openAboutInformation, closeAboutInformation] = useOpenState(false, false);

    // Force Mobile state
    const [remember, setRemember] = useState(false);
    const [isMobileViewOpen, openMobileView, closeMobileView] = useOpenState(false, false);

    // Specialties
    const specialties = useSelector(specialtiesReducer);
    const [activeSpecialty, setActiveSpecialty] = useState('');

    // Menu state
    const [anchorEl, setAnchorEl] = useState(null);
    const button = useRef(null);
    const open = Boolean(anchorEl);

    // Active user modules
    const [activeModules] = useActiveModules();

    // Config
    const centralPageEnabled = getConfig('modules.centralPage.enabled');
    const adminModuleEnabled = getConfig('adminModuleEnabled', false);
    const adminModuleEnabledExceptionUsers = getConfig('adminModuleEnabledExceptionUsers', []);
    const smallDeviceSwitchEnable = getConfig('smallDevice.smallDeviceSwitchEnable', false);

    const handleKeyPress = useCallback((event) => {
        if (event.code === 'KeyM' && event.ctrlKey) {
            event.preventDefault();
            setAnchorEl(button.current);
        }
        if (event.code === 'KeyP' && event.ctrlKey) {
            event.preventDefault();
            openUserProperties();
        }
        if (event.code === 'KeyT' && event.ctrlKey && event.altKey) {
            event.preventDefault();
            window._switchTheme();
            dispatch(saveCurrentTheme(window._getTheme()));
        }
    }, []);

    // Get current specialties
    useEffect(() => {
        if (!_.isEmpty(specialties)) {
            const spec = _.find(specialties, { isActive: true });
            if (spec) setActiveSpecialty(spec.__id);
        }
    }, [specialties]);

    // Event Listener
    useEventListener('keydown', handleKeyPress);

    const handleClick = (event) => {
        if (agylePreference?.topMenuMode === 'mixed' && whiteboardMode && onClick) {
            onClick();
        } else {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleMouseMove = (event) => {
        if (agylePreference?.topMenuMode === 'mixed' && whiteboardMode) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = (e) => {
        const tag = e?.target?.tagName?.toLowerCase();
        if (tag !== 'input' && tag !== 'svg') {
            setAnchorEl(null);
        }
    };

    const handleMoveClose = () => {
        if (agylePreference?.topMenuMode === 'mixed' && whiteboardMode) {
            setAnchorEl(null);
        }
    };

    const handleMenuOption = (option) => {
        handleClose();
        // eslint-disable-next-line default-case
        switch (option) {
            case 'logout':
                window.auth.logout('/');
                break;
            case 'properties':
                openUserProperties();
                break;
            case 'agyleHub':
                history.push(CENTRAL_PAGE_PATH);
                break;
            case 'switchTheme':
                window._switchTheme();
                dispatch(saveCurrentTheme(window._getTheme()));
                break;
            case 'about':
                openAboutInformation();
                break;
            case 'announcement':
                openAnnouncements();
                break;
            case 'adminPage':
                if (blockContext) {
                    dispatch(
                        setSnackbar({
                            type: 'warning',
                            message: t('Close the current sepsis pathway to perform this operation.'),
                        }),
                    );
                } else {
                    history.push('/admin');
                }
                break;
            case 'backToMct':
                history.push('/wards');
                break;
            case 'mobileView':
                openMobileView();
                break;
        }
    };

    const handleChangeView = () => {
        window.sessionStorage.setItem('agyle_sessionForceMobileView', 'true');
        if (remember) window.localStorage.setItem('agyle_forceMobileView', 'true');

        history.push('/');
    };

    const renderMobileBody = () => {
        return (
            <>
                <div>{t('Are you sure you want to force change to mobile view for this device?')}</div>
                <div className={classes.divRow}>
                    <AgyleBlockSwitch onChange={setRemember} value={remember} />

                    <FormLabel style={{ marginLeft: 5 }}>{t('Remember device settings')}</FormLabel>
                </div>
            </>
        );
    };

    const getActiveSpecialty = () => {
        return _.find(specialties, { __id: activeSpecialty }) || null;
    };

    const handleChangeActiveSpecialty = (_e, val) => {
        setActiveSpecialty(val?.__id);
        dispatch(saveSpecialties(UserPropertiesUtils.buildActiveSpecialties(val?.__id, specialties)));
    };

    const getActiveSpecialtyTitle = useCallback(() => {
        return (
            <Stack direction="row" className={classes.specTitle}>
                <Typography variant="body2">{_.capitalize(getActiveSpecialty()?.__description || 'No specialty selected')}</Typography>

                <Typography variant="subtitle2" className={classes.specsCountIndicator}>
                    {specialties.length}
                </Typography>
            </Stack>
        );
    }, [activeSpecialty, specialties]);

    return (
        <>
            <IconButton disableRipple onClick={handleClick} onMouseMove={handleMouseMove} ref={button} size={size}>
                <AgyleLogoIcon className={clsx(classes.appLogo, size)} />
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                id={id}
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                PaperProps={{
                    elevation: 0,
                    className: 'scroll-normal-white',
                    sx: {
                        pr: 1,
                        minWidth: centralPageEnabled && 270,
                        overflow: 'visible',
                        borderRadius: '10px',
                        backgroundColor: centralPageEnabled && theme?.palette?.background?.paperDarker,
                        '& .MuiAvatar-root': {
                            width: 32,
                            height: 32,
                            ml: -0.5,
                            mr: 1,
                        },
                    },
                }}
                BackdropProps={{
                    invisible: whiteboardMode,
                    classes: {
                        root: classes.backDrop,
                    },
                }}
                transformOrigin={transform}
                anchorOrigin={anchor}
                MenuListProps={{
                    onPointerLeave: handleMoveClose,
                }}
            >
                {!centralPageEnabled && (
                    <MenuItem className={clsx(classes.menuItem, 'name')} disableRipple>
                        <ListItemIcon>
                            <Avatar fontSize="small" />
                        </ListItemIcon>

                        {`${userObject.firstName} ${userObject.familyName}`}
                    </MenuItem>
                )}

                {!centralPageEnabled && (
                    <div style={{ padding: 5, marginBottom: 5 }}>
                        <AgyleDictionaryField
                            options={specialties}
                            filterOptions={filterOptions}
                            value={getActiveSpecialty()}
                            onChange={handleChangeActiveSpecialty}
                            size="small"
                            noBorder={false}
                            withBackground={false}
                            label={t('Spec')}
                            disableClearable
                            textFieldStyle={classes.textFieldStyle}
                            inputLabelClass={classes.textFieldLabelStyle}
                        />
                    </div>
                )}

                {centralPageEnabled && (
                    <Stack className={clsx(classes.divRow, classes.avatarWithName)} sx={{ mb: 2 }}>
                        <UserAvatar size="medium" />

                        <Typography variant="body1" fontWeight={600}>
                            {`${userObject.firstName} ${userObject.familyName}`}
                        </Typography>

                        <UserActiveSpecialties showOnlyActive />
                    </Stack>
                )}

                {centralPageEnabled && (
                    <Box sx={{ pl: 1, pr: 1.5, mb: 1 }}>
                        <AgyleMenuList
                            options={specialties}
                            value={getActiveSpecialty()}
                            buttonLabel={getActiveSpecialtyTitle()}
                            onSelectItem={handleChangeActiveSpecialty}
                        />
                    </Box>
                )}

                {centralPageEnabled && (
                    <Stack
                        sx={{ p: 1, my: 0.5, pr: 0.1 }}
                        gap={1.5}
                        direction="row"
                        className={clsx(classes.modulesContainer, 'scroll-normal-white')}
                    >
                        <ActiveModules activeModules={activeModules} tileSize="small" paperBackground />
                    </Stack>
                )}

                {centralPageEnabled && (
                    <MenuItem onClick={handleMenuOption.bind(this, 'agyleHub')} className={clsx(classes.menuItem, 'agyleHub')}>
                        <ListItemIcon className={classes.hubIcon}>
                            <AgyleNamedIcon name="agyleHub" />
                        </ListItemIcon>

                        {t('Go to Agyle Home')}
                    </MenuItem>
                )}

                <Divider />

                {!adminModuleEnabled && dispatch(hasPermission('CAN_ADD_ANNOUNCEMENT')) && (
                    <MenuItem onClick={handleMenuOption.bind(this, 'announcement')} className={classes.menuItem}>
                        <ListItemIcon>
                            <AgyleNamedIcon name={'announcements'} />
                        </ListItemIcon>
                        {t('Announcements')}
                    </MenuItem>
                )}

                {(adminModuleEnabled || adminModuleEnabledExceptionUsers.includes(userObject.userName)) &&
                    dispatch(canEnterAdminPage()) && (
                        <MenuItem className={classes.menuItem} onClick={handleMenuOption.bind(this, 'adminPage')}>
                            <ListItemIcon>
                                <AdminPanelSettingsIcon fontSize="small" />
                            </ListItemIcon>
                            {t('Administration')}
                        </MenuItem>
                    )}

                <MenuItem onClick={handleMenuOption.bind(this, 'properties')} className={classes.menuItem}>
                    <ListItemIcon>
                        <Settings fontSize="small" />
                    </ListItemIcon>
                    {t('Agyle Settings')}
                </MenuItem>

                <MenuItem onClick={handleMenuOption.bind(this, 'switchTheme')} className={classes.menuItem}>
                    <ListItemIcon>
                        <ChangeCircle fontSize="small" />
                    </ListItemIcon>
                    {t('Switch theme')}
                </MenuItem>

                <MenuItem onClick={handleMenuOption.bind(this, 'about')} className={classes.menuItem}>
                    <ListItemIcon>
                        <InfoIcon fontSize="small" />
                    </ListItemIcon>
                    {t('About Agyle')}
                </MenuItem>

                {smallDeviceSwitchEnable && (
                    <MenuItem onClick={handleMenuOption.bind(this, 'mobileView')} className={classes.menuItem}>
                        <ListItemIcon>
                            <StayCurrentPortraitIcon fontSize="small" />
                        </ListItemIcon>
                        {t('Mobile View')}
                    </MenuItem>
                )}

                <Divider />

                {whiteboardMode && (
                    <div>
                        <MenuItem
                            className={classes.menuItem}
                            onClick={handleMenuOption.bind(this, 'backToMct')}
                            disabled={
                                !dispatch(
                                    hasOneOfPermission([
                                        Permissions.AGYLE_CAN_ACCESS_MCT_ADMIN,
                                        Permissions.AGYLE_CAN_MANAGE_WARD_MAP,
                                        Permissions.AGYLE_CAN_PUBLISH_WARD_MAP,
                                        Permissions.AGYLE_CAN_VIEW_ONLY_WARD_MAP,
                                    ]),
                                )
                            }
                        >
                            <ListItemIcon>
                                <MapsHomeWorkIcon fontSize="small" />
                            </ListItemIcon>
                            {t('Back to Ward List')}
                        </MenuItem>

                        <Divider />
                    </div>
                )}

                <MenuItem onClick={handleMenuOption.bind(this, 'logout')} className={classes.menuItem}>
                    <ListItemIcon>
                        <Logout fontSize="small" />
                    </ListItemIcon>
                    {t('Logout')}
                </MenuItem>
            </Menu>

            {isUserPropertiesOpen && <UserProperties open={isUserPropertiesOpen} onClose={closeUserProperties} />}

            {isAboutInformationOpen && <AboutInfo open={isAboutInformationOpen} onClose={closeAboutInformation} />}

            {isAnnouncementsOpen && <AnnouncementPanel open={isAnnouncementsOpen} onClose={closeAnnouncements} />}

            <MessageBox
                onClose={closeMobileView}
                title={t('Confirmation')}
                haveAccept={{ yes: t('Submit'), no: t('Close') }}
                onAccept={handleChangeView}
                messageObject={renderMobileBody()}
                open={isMobileViewOpen}
            />
        </>
    );
};

export default AgyleMenuIcon;
